import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2 } from "lucide-react";
import { SAYISAL_TIERS } from "@/lib/constants";
import { toast } from "sonner";
import { PasswordField } from "@/components/PasswordField";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", sayisal_tier: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwStrength, setPwStrength] = useState({ score: 0, valid: false, length: 0 });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const hasClaimTicket = Boolean(
    searchParams.get("claim_ticket") ||
    (typeof window !== "undefined" && sessionStorage.getItem("sz_sayisal_claim_ticket")),
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const nameOk = form.name.trim().length >= 2;
  const emailOk = /^\S+@\S+\.\S+$/.test(form.email.trim());
  const confirmOk = confirmPassword.length > 0 && confirmPassword === form.password;
  const canSubmit = nameOk && emailOk && pwStrength.valid && confirmOk && !loading;

  const claimPriorLead = async () => {
    // Prefer URL param, fall back to sessionStorage (Track B handoff).
    const ticket =
      searchParams.get("claim_ticket") ||
      sessionStorage.getItem("sz_sayisal_claim_ticket");
    if (!ticket) return;
    try {
      await api.post("/sayisal/claim", { claim_ticket: ticket });
      sessionStorage.removeItem("sz_sayisal_claim_ticket");
      localStorage.removeItem("sz_sayisal_lead_id");
      toast.success("Önceki tanılama oturumun hesabına bağlandı.");
    } catch {
      /* non-fatal — user is already registered; they can retry later */
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    setLoading(true);
    try {
      const u = await register({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        sayisal_tier: form.sayisal_tier || null,
      });
      toast.success("Hesabın oluşturuldu — e-posta adresine doğrulama bağlantısı gönderdik.");
      // Attempt best-effort claim of any prior anonymous diagnostic.
      await claimPriorLead();
      nav(u.role === "admin" ? "/admin" : "/panel?claimed=true", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="register-page">
      <div className="hidden md:flex bg-zinc-50 border-r border-zinc-200 p-10 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
          </Link>
          <p className="mt-2 ml-10 text-xs text-zinc-500 tracking-wide">
            Türkiye&apos;nin öğrencileri için geliştirildi.
          </p>
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-3xl font-bold tracking-tight leading-tight text-zinc-900">
            Ücretsiz. Hızlı. Kredi kartı gerekmez.
          </h2>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            Sınıfını seç, ilk sorunu yaz. SuperZeka konuyu sana uygun bir dille adım adım anlatır.
          </p>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">© {new Date().getFullYear()} SuperZeka</div>
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#1E3A8A]/10 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex md:hidden items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Ücretsiz hesap oluştur
          </h1>
          <p className="mt-2 text-sm text-zinc-600">Sınıfını seç ve öğrenmeye başla.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Ad Soyad</Label>
              <Input
                id="name"
                required
                data-testid="register-name"
                value={form.name}
                onChange={set("name")}
                className="rounded-xl"
                placeholder="Adın Soyadın"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="register-email"
                value={form.email}
                onChange={set("email")}
                className="rounded-xl"
                placeholder="ornek@eposta.com"
              />
            </div>
            <PasswordField
              id="password"
              value={form.password}
              onChange={(v) => setForm((f) => ({ ...f, password: v }))}
              onStrengthChange={setPwStrength}
              onGenerated={(pw) => setConfirmPassword(pw)}
              userInputs={[form.name, form.email]}
              testIdPrefix="register-password"
            />

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Şifre (tekrar)</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-xl"
                placeholder="Şifreyi tekrar yaz"
                autoComplete="new-password"
                spellCheck={false}
                data-testid="register-password-confirm"
              />
              {confirmPassword.length > 0 && confirmOk && (
                <div className="text-xs text-emerald-600 flex items-center gap-1" data-testid="register-password-match">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                  </svg>
                  Şifreler eşleşiyor
                </div>
              )}
              {confirmPassword.length > 0 && !confirmOk && (
                <div className="text-xs text-red-600" data-testid="register-password-mismatch">
                  Şifreler eşleşmiyor
                </div>
              )}
            </div>
            {!hasClaimTicket && (
              <div className="space-y-1.5" data-testid="register-sayisal-tier-block">
                <Label>Sayısal hedefin (opsiyonel)</Label>
                <Select
                  value={form.sayisal_tier}
                  onValueChange={(v) => setForm((f) => ({ ...f, sayisal_tier: v }))}
                >
                  <SelectTrigger data-testid="register-sayisal-tier" className="rounded-xl">
                    <SelectValue placeholder="Sınavını seç" />
                  </SelectTrigger>
                  <SelectContent>
                    {SAYISAL_TIERS.map((t) => (
                      <SelectItem
                        key={t.value}
                        value={t.value}
                        data-testid={`sayisal-tier-opt-${t.value}`}
                      >
                        {t.label} — <span className="text-zinc-500">{t.hint}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  SuperZeka şu an yalnız Sayısal (LGS · TYT · AYT) hedefli akış üzerinde
                  çalışıyor. İçerik motoru dersin için aktif olduğunda haber vereceğiz.
                </p>
              </div>
            )}
            {hasClaimTicket && (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                data-testid="register-claim-notice"
              >
                Önceki tanılama izin taşınacak — sınıf seçmene gerek yok.
              </div>
            )}

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="register-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="register-submit"
              className="w-full h-11 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ücretsiz Kayıt Ol"}
            </Button>

            <p className="text-xs text-zinc-500 leading-relaxed">
              Kayıt olarak{" "}
              <Link to="/kullanim-sartlari" className="text-[#1E3A8A] hover:underline">
                Kullanım Şartları
              </Link>{" "}
              ve{" "}
              <Link to="/gizlilik" className="text-[#1E3A8A] hover:underline">
                Gizlilik Politikası
              </Link>
              &apos;nı kabul etmiş olursun.
            </p>
          </form>

          <p className="mt-6 text-sm text-zinc-600 text-center">
            Hesabın var mı?{" "}
            <Link to="/giris" className="text-[#1E3A8A] font-medium hover:underline" data-testid="link-to-login">
              Giriş yap
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
